import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X, Package, ShoppingBag, MessageSquare, Upload, MessageCircle, Send, Sparkles, Download } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";

const EMPTY = {
  name: "",
  category: "Growth Peptides",
  short_description: "",
  description: "",
  price: 0,
  purity: "≥ 99%",
  size: "5mg",
  sku: "",
  stock: 100,
  image_url: "https://images.unsplash.com/photo-1675336753554-5493966a6970?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  is_active: true,
  variants: [
    { label: "5mg", price: 0, in_stock: true },
    { label: "10mg", price: 0, in_stock: true },
    { label: "20mg", price: 0, in_stock: true },
    { label: "", price: 0, in_stock: true },
    { label: "", price: 0, in_stock: true },
    { label: "", price: 0, in_stock: true },
    { label: "", price: 0, in_stock: true },
    { label: "", price: 0, in_stock: true },
    { label: "", price: 0, in_stock: true },
  ],
};

const VARIANT_SLOTS = 9;

const STATUSES = ["pending", "processing", "shipped", "cancelled"];

export default function Admin() {
  const [tab, setTab] = useState("products");

  // Heartbeat so visitors see "ONLINE NOW"
  useEffect(() => {
    const ping = () => api.post("/admin/presence").catch(() => {});
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen">
      <section className="border-b-[3px] border-black bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono-ui text-xs text-[#00d4ff]">— ADMIN DASHBOARD</div>
            <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Control room<span className="text-[#ff1493]">.</span></h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <TabBtn Icon={Package} id="products" tab={tab} setTab={setTab}>Products</TabBtn>
            <TabBtn Icon={Sparkles} id="offers" tab={tab} setTab={setTab}>Offers</TabBtn>
            <TabBtn Icon={ShoppingBag} id="orders" tab={tab} setTab={setTab}>Orders</TabBtn>
            <TabBtn Icon={MessageCircle} id="chats" tab={tab} setTab={setTab}>Chats</TabBtn>
            <TabBtn Icon={MessageSquare} id="messages" tab={tab} setTab={setTab}>Messages</TabBtn>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {tab === "products" && <ProductsPanel />}
        {tab === "offers" && <OffersPanel />}
        {tab === "orders" && <OrdersPanel />}
        {tab === "chats" && <ChatsPanel />}
        {tab === "messages" && <MessagesPanel />}
      </section>
    </div>
  );
}

function TabBtn({ Icon, id, tab, setTab, children }) {
  const on = tab === id;
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      className={`inline-flex items-center gap-2 px-4 py-2 border-[3px] border-white font-mono-ui text-xs font-bold ${on ? "bg-[#ff1493] text-white" : "bg-white text-black"}`}
    >
      <Icon className="w-4 h-4" strokeWidth={2.5} /> {children.toString().toUpperCase()}
    </button>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  };
  useEffect(load, []);

  const onEdit = (p) => { setEditing(p); setShowForm(true); };
  const onNew = () => { setEditing(null); setShowForm(true); };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="font-display text-3xl">Products ({products.length})</div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onImported={load} />
          {products.length > 0 && (
            <button
              onClick={async () => {
                if (!window.confirm(`Delete ALL ${products.length} products? This cannot be undone.`)) return;
                try {
                  await api.delete("/admin/products");
                  toast.success("All products deleted");
                  load();
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              data-testid="admin-wipe-products"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-[10px] font-bold text-black hover:bg-black hover:text-white"
            >
              <Trash2 className="w-3.5 h-3.5" /> WIPE ALL
            </button>
          )}
          <button onClick={onNew} data-testid="admin-new-product" className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff1493] text-white border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-xs font-bold">
            <Plus className="w-4 h-4" /> NEW PRODUCT
          </button>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-ui text-xs">LOADING…</div>
      ) : (
        <div className="border-[3px] border-black overflow-x-auto bg-white">
          <table className="min-w-full">
            <thead>
              <tr className="bg-black text-white font-mono-ui text-[10px]">
                <th className="text-left px-4 py-3">NAME</th>
                <th className="text-left px-4 py-3">CATEGORY</th>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-right px-4 py-3">PRICE</th>
                <th className="text-right px-4 py-3">STOCK</th>
                <th className="text-right px-4 py-3">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t-2 border-black/15 hover:bg-[#f4f4f5]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover border-[2px] border-black" />
                      <div>
                        <div className="font-display text-sm">{p.name}</div>
                        <div className="font-mono-ui text-[10px] text-black/50">{p.size}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-ui text-[11px]">{p.category}</td>
                  <td className="px-4 py-3 font-mono-ui text-[11px]">{p.sku}</td>
                  <td className="px-4 py-3 text-right font-display">£{p.price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono-ui text-xs">{p.stock}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => onEdit(p)} data-testid={`admin-edit-${p.id}`} className="w-8 h-8 border-[2px] border-black flex items-center justify-center hover:bg-[#00d4ff]"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => onDelete(p.id)} data-testid={`admin-del-${p.id}`} className="w-8 h-8 border-[2px] border-black flex items-center justify-center hover:bg-black hover:text-white"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ProductForm
          initial={editing || EMPTY}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const normalisedInitial = {
    ...initial,
    variants: Array.isArray(initial.variants) && initial.variants.length > 0
      ? Array.from({ length: VARIANT_SLOTS }, (_, i) => {
          const v = initial.variants[i];
          return v ? { label: v.label || "", price: v.price || 0, in_stock: v.in_stock !== false } : { label: "", price: 0, in_stock: true };
        })
      : [
          { label: initial.size || "5mg", price: Number(initial.price) || 0, in_stock: true },
          { label: "2× pack", price: Number(initial.price) * 1.9 || 0, in_stock: true },
          { label: "3× pack", price: Number(initial.price) * 2.7 || 0, in_stock: true },
          ...Array.from({ length: VARIANT_SLOTS - 3 }, () => ({ label: "", price: 0, in_stock: true })),
        ],
  };
  const [f, setF] = useState(normalisedInitial);
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((prev) => ({ ...prev, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanVariants = (f.variants || [])
        .filter((v) => v.label && v.label.trim() !== "")
        .map((v) => ({
          label: v.label.trim(),
          price: Number(v.price) || 0,
          in_stock: v.in_stock !== false,
        }));
      const payload = {
        ...f,
        price: Number(f.price),
        stock: Number(f.stock),
        variants: cleanVariants,
      };
      if (initial.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const updVariant = (i, k) => (e) => {
    const v = k === "price" ? Number(e.target.value) : k === "in_stock" ? e.target.checked : e.target.value;
    setF((prev) => {
      const next = [...(prev.variants || [])];
      next[i] = { ...(next[i] || { label: "", price: 0, in_stock: true }), [k]: v };
      return { ...prev, variants: next };
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start md:items-center justify-center p-4 overflow-auto">
      <form onSubmit={save} className="w-full max-w-2xl bg-white border-[3px] border-black shadow-brut p-6 md:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl">{initial.id ? "Edit product" : "New product"}</div>
          <button type="button" onClick={onClose} className="w-9 h-9 border-[3px] border-black flex items-center justify-center hover:bg-black hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name" value={f.name} onChange={upd("name")} testid="pf-name" required />
          <Field label="Category" value={f.category} onChange={upd("category")} testid="pf-cat" required />
          <Field label="SKU" value={f.sku} onChange={upd("sku")} testid="pf-sku" required />
          <Field label="Stock" type="number" value={f.stock} onChange={upd("stock")} testid="pf-stock" required />
          <Field label="Purity" value={f.purity} onChange={upd("purity")} testid="pf-purity" />
          <ImageField value={f.image_url} onChange={(v) => setF((p) => ({ ...p, image_url: v }))} />
        </div>
        <Field label="Short description" value={f.short_description} onChange={upd("short_description")} testid="pf-short" required />
        <div>
          <label className="font-mono-ui text-[10px] block mb-1">LONG DESCRIPTION</label>
          <textarea rows={4} value={f.description} onChange={upd("description")} data-testid="pf-desc" className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs resize-none" required />
        </div>

        <div className="border-[3px] border-black p-4 bg-[#f4f4f5]">
          <div className="font-mono-ui text-[10px] mb-2">SIZE / PACK OPTIONS · UP TO 9 (SHOWN AS DROPDOWN · FIRST FILLED OPTION'S PRICE IS THE HEADLINE PRICE)</div>
          <div className="space-y-2">
            {Array.from({ length: VARIANT_SLOTS }, (_, i) => i).map((i) => {
              const inStock = f.variants?.[i]?.in_stock !== false;
              return (
                <div key={i} className="grid grid-cols-[24px_1fr_110px_110px] gap-2 items-center">
                  <div className="font-mono-ui text-[10px] text-black/40 text-right">{i + 1}</div>
                  <input
                    type="text"
                    placeholder={`Option ${i + 1} label (e.g. 10mg)`}
                    value={f.variants?.[i]?.label ?? ""}
                    onChange={updVariant(i, "label")}
                    data-testid={`pf-variant-label-${i}`}
                    className={`w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs bg-white ${!inStock ? "line-through text-black/50" : ""}`}
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="£ price"
                    value={f.variants?.[i]?.price ?? 0}
                    onChange={updVariant(i, "price")}
                    data-testid={`pf-variant-price-${i}`}
                    className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs bg-white"
                  />
                  <label className={`inline-flex items-center gap-2 border-[3px] border-black px-2 py-2 cursor-pointer font-mono-ui text-[10px] font-bold ${inStock ? "bg-white" : "bg-black text-white"}`}>
                    <input
                      type="checkbox"
                      checked={!inStock}
                      onChange={(e) => {
                        const soldOut = e.target.checked;
                        setF((prev) => {
                          const next = [...(prev.variants || [])];
                          next[i] = { ...(next[i] || { label: "", price: 0 }), in_stock: !soldOut };
                          return { ...prev, variants: next };
                        });
                      }}
                      data-testid={`pf-variant-soldout-${i}`}
                      className="w-3 h-3 accent-[#ff1493]"
                    />
                    SOLD OUT
                  </label>
                </div>
              );
            })}
          </div>
          <div className="font-mono-ui text-[10px] text-black/60 mt-2">
            Leave a label empty to hide that option. The first option with a label auto-fills the product's base price.
          </div>
        </div>
        <label className="inline-flex items-center gap-2 font-mono-ui text-xs">
          <input type="checkbox" checked={f.is_active} onChange={upd("is_active")} className="w-4 h-4 accent-black" />
          ACTIVE / LISTED
        </label>
        <div className="pt-2 flex gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border-[3px] border-black font-mono-ui text-xs font-bold">CANCEL</button>
          <button type="submit" disabled={saving} data-testid="pf-save" className="flex-1 px-4 py-2 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold disabled:opacity-60">
            {saving ? "SAVING…" : "SAVE PRODUCT"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="font-mono-ui text-[10px] block mb-1">{label.toUpperCase()}</label>
      <input {...rest} data-testid={testid} className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs" />
    </div>
  );
}

function ExportImportButtons({ onImported }) {
  const [importing, setImporting] = useState(false);

  const doExport = async () => {
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `ukpr-catalog-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.products.length} products + ${data.offers.length} offers`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const mode = window.confirm(
      "Click OK to MERGE (upsert by SKU, keeps anything not in the file).\n\nClick Cancel to REPLACE (wipes current catalog, imports only what's in the file).",
    )
      ? "merge"
      : "replace";
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      setImporting(true);
      const { data } = await api.post("/admin/import", {
        products: parsed.products || [],
        offers: parsed.offers || [],
        mode,
      });
      toast.success(
        `Import ${mode.toUpperCase()}: ${data.created} new, ${data.updated} updated, ${data.offers_imported} offers`,
      );
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Invalid file");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <button
        onClick={doExport}
        data-testid="admin-export"
        className="inline-flex items-center gap-2 px-3 py-2 bg-[#00d4ff] border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
      >
        <Download className="w-4 h-4" /> EXPORT CATALOG
      </button>
      <label className={`inline-flex items-center gap-2 px-3 py-2 bg-white border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-xs font-bold cursor-pointer ${importing ? "opacity-60 pointer-events-none" : ""}`}>
        <Upload className="w-4 h-4" />
        {importing ? "IMPORTING…" : "IMPORT CATALOG"}
        <input type="file" accept="application/json,.json" onChange={doImport} data-testid="admin-import-file" className="hidden" />
      </label>
    </>
  );
}

function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8MB or smaller");
      return;
    }
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      const { data: res } = await api.post("/admin/uploads/image", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // backend returns a relative /api/files/... URL; make it absolute for display
      const absolute = res.url.startsWith("http") ? res.url : `${BACKEND}${res.url}`;
      onChange(absolute);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="font-mono-ui text-[10px] block mb-1">PRODUCT IMAGE</label>
      <div className="flex items-start gap-3">
        {value ? (
          <img src={value} alt="preview" className="w-16 h-16 object-cover border-[3px] border-black shrink-0" />
        ) : (
          <div className="w-16 h-16 border-[3px] border-dashed border-black flex items-center justify-center text-black/40 font-mono-ui text-[9px] shrink-0">NO IMG</div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste image URL or upload →"
            data-testid="pf-img"
            className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs"
            required
          />
          <label className={`inline-flex items-center gap-2 px-3 py-2 border-[3px] border-black bg-[#00d4ff] shadow-brut-sm btn-press font-mono-ui text-xs font-bold cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            <Upload className="w-4 h-4" strokeWidth={2.5} />
            {uploading ? "UPLOADING…" : "UPLOAD IMAGE"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={onFile}
              data-testid="pf-img-upload"
              className="hidden"
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/orders").then((r) => { setOrders(r.data); setLoading(false); });
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalRevenue = useMemo(() => orders.filter(o => o.status !== "cancelled").reduce((s,o) => s + o.total, 0), [orders]);

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Stat label="ORDERS" value={orders.length} color="bg-white" />
        <Stat label="REVENUE" value={`£${totalRevenue.toFixed(2)}`} color="bg-[#ff1493] text-white" />
        <Stat label="PENDING" value={orders.filter(o => o.status === "pending").length} color="bg-[#facc15]" />
      </div>
      {loading ? <div className="font-mono-ui text-xs">LOADING…</div> : (
        <div className="space-y-4">
          {orders.length === 0 && <div className="border-[3px] border-black p-10 text-center font-mono-ui text-xs">NO ORDERS YET.</div>}
          {orders.map((o) => (
            <div key={o.id} className="border-[3px] border-black bg-white p-5 shadow-brut-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <div className="font-mono-ui text-[10px] text-black/50">ORDER · {new Date(o.created_at).toLocaleString()}</div>
                  <div className="font-display text-lg">#{o.id.slice(0, 8).toUpperCase()} · {o.user_email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`admin-order-status-${o.id}`} className="border-[3px] border-black px-2 py-1.5 font-mono-ui text-[11px] bg-white">
                    {STATUSES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                  <div className="font-display text-xl text-[#ff1493]">£{o.total.toFixed(2)}</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 border-t-2 border-black/10 pt-3">
                <div>
                  <div className="font-mono-ui text-[10px] text-black/50 mb-1">ITEMS</div>
                  <ul className="text-sm space-y-1">
                    {o.items.map((it, i) => <li key={i} className="flex justify-between"><span className="truncate pr-3">{it.name} × {it.quantity}</span><span className="font-mono-ui text-[11px]">£{(it.price * it.quantity).toFixed(2)}</span></li>)}
                  </ul>
                </div>
                <div>
                  <div className="font-mono-ui text-[10px] text-black/50 mb-1">SHIP TO</div>
                  <div className="text-sm">
                    {o.shipping.full_name}<br />
                    {o.shipping.address_line1}{o.shipping.address_line2 ? `, ${o.shipping.address_line2}` : ""}<br />
                    {o.shipping.city}, {o.shipping.postcode}, {o.shipping.country}<br />
                    <span className="font-mono-ui text-[11px]">{o.shipping.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesPanel() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/contacts").then((r) => { setList(r.data); setLoading(false); }); }, []);
  return (
    <div>
      <div className="font-display text-3xl mb-6">Messages ({list.length})</div>
      {loading ? <div className="font-mono-ui text-xs">LOADING…</div> : list.length === 0 ? (
        <div className="border-[3px] border-black p-10 text-center font-mono-ui text-xs">NO MESSAGES YET.</div>
      ) : (
        <div className="space-y-4">
          {list.map((m) => (
            <div key={m.id} className="border-[3px] border-black bg-white p-5 shadow-brut-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-mono-ui text-[10px] text-black/50">FROM</div>
                  <div className="font-display text-lg">{m.name} · <span className="text-[#ff1493]">{m.email}</span></div>
                </div>
                <div className="font-mono-ui text-[11px] text-black/60">{new Date(m.created_at).toLocaleString()}</div>
              </div>
              <div className="mt-3 font-display text-base">RE: {m.subject}</div>
              <p className="mt-2 text-sm leading-relaxed">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className={`${color} border-[3px] border-black p-5 shadow-brut-sm`}>
      <div className="font-mono-ui text-[10px]">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

function OffersPanel() {
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [o, p] = await Promise.all([
      api.get("/admin/offers"),
      api.get("/products"),
    ]);
    setOffers(o.data);
    setProducts(p.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const EMPTY_OFFER = {
    title: "",
    subtitle: "",
    cta_label: "SHOP NOW",
    bg_image_url: "",
    product_id: "",
    href: "",
    position: offers.length,
    accent: "pink",
    is_active: true,
  };

  const del = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/admin/offers/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="font-display text-3xl">Special offers ({offers.length})</div>
        <button
          onClick={() => setEditing(EMPTY_OFFER)}
          data-testid="admin-new-offer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff1493] text-white border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
        >
          <Plus className="w-4 h-4" /> NEW OFFER
        </button>
      </div>

      {loading ? (
        <div className="font-mono-ui text-xs">LOADING…</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {offers.map((o) => (
            <div key={o.id} className="border-[3px] border-black bg-white shadow-brut-sm">
              <div className="relative aspect-[16/9] bg-[#f4f4f5] border-b-[3px] border-black overflow-hidden">
                {o.bg_image_url && <img src={o.bg_image_url} alt={o.title} className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-black/35" />
                <div className="absolute inset-0 p-4 text-white flex flex-col justify-between">
                  <div className="inline-flex items-center gap-1 px-2 py-1 border-[2px] border-black bg-[#ff1493] font-mono-ui text-[10px] font-bold self-start">
                    <Sparkles className="w-3 h-3" strokeWidth={2.5} /> {o.accent?.toUpperCase() || "PINK"}
                  </div>
                  <div className="font-display text-xl">{o.title}</div>
                </div>
              </div>
              <div className="p-4 space-y-2">
                <div className="font-mono-ui text-[11px] text-black/70 line-clamp-2">{o.subtitle || "—"}</div>
                <div className="font-mono-ui text-[10px] text-black/50">POS {o.position} · {o.is_active ? "ACTIVE" : "HIDDEN"}</div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setEditing(o)} data-testid={`admin-offer-edit-${o.id}`} className="flex-1 px-3 py-2 border-[3px] border-black font-mono-ui text-xs font-bold hover:bg-[#00d4ff]">EDIT</button>
                  <button onClick={() => del(o.id)} data-testid={`admin-offer-del-${o.id}`} className="w-10 h-10 border-[3px] border-black flex items-center justify-center hover:bg-black hover:text-white"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <OfferForm
          initial={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function OfferForm({ initial, products, onClose, onSaved }) {
  const [f, setF] = useState({ ...initial, product_id: initial.product_id || "", href: initial.href || "" });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((prev) => ({ ...prev, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: f.title,
        subtitle: f.subtitle || "",
        cta_label: f.cta_label || "SHOP NOW",
        bg_image_url: f.bg_image_url || "",
        product_id: f.product_id || null,
        href: f.href || null,
        position: Number(f.position) || 0,
        accent: f.accent || "pink",
        is_active: !!f.is_active,
      };
      if (initial.id) await api.put(`/admin/offers/${initial.id}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start md:items-center justify-center p-4 overflow-auto">
      <form onSubmit={save} className="w-full max-w-2xl bg-white border-[3px] border-black shadow-brut p-6 md:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl">{initial.id ? "Edit offer" : "New offer"}</div>
          <button type="button" onClick={onClose} className="w-9 h-9 border-[3px] border-black flex items-center justify-center hover:bg-black hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <Field label="Headline" value={f.title} onChange={upd("title")} testid="of-title" required />
        <div>
          <label className="font-mono-ui text-[10px] block mb-1">SUB-COPY</label>
          <textarea rows={2} value={f.subtitle} onChange={upd("subtitle")} data-testid="of-subtitle" className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs resize-none" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CTA label" value={f.cta_label} onChange={upd("cta_label")} testid="of-cta" />
          <div>
            <label className="font-mono-ui text-[10px] block mb-1">ACCENT COLOUR</label>
            <select value={f.accent} onChange={upd("accent")} data-testid="of-accent" className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs bg-white">
              <option value="pink">Pink</option>
              <option value="blue">Blue</option>
              <option value="yellow">Yellow</option>
              <option value="dark">Dark</option>
            </select>
          </div>
        </div>
        <Field label="Background image URL" value={f.bg_image_url} onChange={upd("bg_image_url")} testid="of-img" />

        <div>
          <label className="font-mono-ui text-[10px] block mb-1">LINK TO PRODUCT (CTA DESTINATION)</label>
          <select value={f.product_id} onChange={upd("product_id")} data-testid="of-product" className="w-full border-[3px] border-black px-3 py-2 font-mono-ui text-xs bg-white">
            <option value="">— no product · use custom URL below —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
            ))}
          </select>
        </div>
        {!f.product_id && (
          <Field label="Or custom URL (optional)" value={f.href} onChange={upd("href")} testid="of-href" placeholder="/shop?category=Healing" />
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Position (sort order)" type="number" value={f.position} onChange={upd("position")} testid="of-position" />
          <label className="inline-flex items-center gap-2 font-mono-ui text-xs mt-6">
            <input type="checkbox" checked={!!f.is_active} onChange={upd("is_active")} data-testid="of-active" className="w-4 h-4 accent-black" />
            ACTIVE / VISIBLE ON HOME
          </label>
        </div>

        <div className="pt-2 flex gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border-[3px] border-black font-mono-ui text-xs font-bold">CANCEL</button>
          <button type="submit" disabled={saving} data-testid="of-save" className="flex-1 px-4 py-2 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold disabled:opacity-60">
            {saving ? "SAVING…" : "SAVE OFFER"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChatsPanel() {
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const r = await api.get("/admin/chats");
    setChats(r.data);
    if (active) {
      const fresh = r.data.find((c) => c.id === active.id);
      if (fresh) setActive(fresh);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/chats/${active.id}/messages`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const deleteChat = async (id) => {
    if (!window.confirm("Delete this chat permanently?")) return;
    await api.delete(`/admin/chats/${id}`);
    if (active?.id === id) setActive(null);
    await load();
  };

  const totalUnread = chats.reduce((s, c) => s + (c.unread_admin || 0), 0);

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Stat label="CONVERSATIONS" value={chats.length} color="bg-white" />
        <Stat label="UNREAD" value={totalUnread} color="bg-[#ff1493] text-white" />
        <Stat label="LAST ACTIVE" value={chats[0] ? new Date(chats[0].updated_at).toLocaleTimeString() : "—"} color="bg-[#facc15]" />
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        <div className="border-[3px] border-black bg-white max-h-[560px] overflow-y-auto">
          {chats.length === 0 && (
            <div className="p-6 text-center font-mono-ui text-xs">NO CHATS YET.</div>
          )}
          {chats.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c)}
              data-testid={`admin-chat-${c.id}`}
              className={`w-full text-left p-4 border-b-2 border-black/15 hover:bg-[#f4f4f5] ${active?.id === c.id ? "bg-[#00d4ff]/30" : ""}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-display text-sm truncate">{c.visitor_name}</div>
                {c.unread_admin > 0 && (
                  <span className="bg-[#ff1493] text-white font-mono-ui text-[9px] font-bold px-1.5 py-0.5 border-[2px] border-black">
                    {c.unread_admin}
                  </span>
                )}
              </div>
              <div className="font-mono-ui text-[10px] text-black/60 truncate">{c.visitor_email}</div>
              <div className="font-mono-ui text-[10px] text-black/40 mt-1">
                {c.messages?.length || 0} msgs · {new Date(c.updated_at).toLocaleString()}
              </div>
            </button>
          ))}
        </div>

        <div className="border-[3px] border-black bg-white min-h-[560px] flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center font-mono-ui text-xs text-black/40">
              SELECT A CONVERSATION TO REPLY
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b-[3px] border-black flex items-center justify-between">
                <div>
                  <div className="font-display text-lg">{active.visitor_name}</div>
                  <div className="font-mono-ui text-[10px] text-black/60">{active.visitor_email} · {(active.messages || []).length} messages</div>
                </div>
                <button onClick={() => deleteChat(active.id)} className="w-8 h-8 border-[2px] border-black flex items-center justify-center hover:bg-black hover:text-white"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f4f4f5]">
                {(active.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] border-[3px] border-black px-3 py-2 text-sm leading-relaxed shadow-brut-sm ${m.sender === "admin" ? "bg-[#00d4ff]" : "bg-white"}`}>
                      <div className="font-mono-ui text-[9px] opacity-70 mb-0.5">
                        {m.sender === "admin" ? "YOU" : active.visitor_name.toUpperCase()} · {new Date(m.created_at).toLocaleTimeString()}
                      </div>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="border-t-[3px] border-black p-3 flex items-center gap-2">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply…"
                  data-testid="admin-chat-reply-input"
                  className="flex-1 border-[3px] border-black px-3 py-2 font-mono-ui text-xs"
                />
                <button
                  type="submit"
                  disabled={sending || !reply.trim()}
                  data-testid="admin-chat-reply-send"
                  className="w-10 h-10 bg-[#ff1493] text-white border-[3px] border-black shadow-brut-sm btn-press flex items-center justify-center disabled:opacity-60"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
